/**
 * Wantai European selling deck — slide model (DR-2026-261; zh-CN added DR-2026-264).
 *
 * The single source of the deck copy, per language. Rendered by Deck.jsx, which
 * picks the slide set from the route's languageroot (/en/deck vs /zh-cn/deck).
 * Copy edits — including resolving the §7.1 / §7.2 gated placeholders when
 * Wantai answers — are a one-file change here, no renderer change.
 *
 * Content follows REF-WANTAI-001 §15.2 (the seven-part spine) with action
 * titles per §15.4. The zh-CN slides are a faithful translation of the en set
 * (Scarlett, native speaker, reviews). `gated: true` slides carry a §7 open item
 * and must not show fabricated figures — they show the placeholder note instead.
 *
 * Slide shape:
 *   { id, kind, kicker, title, lead?, points?[], columns?[{head,body}],
 *     footnote?, gated?, gatedNote? }
 *   kind ∈ cover | problem | two-col | product | platform | proof | ask | standard
 */

const en = [
  {
    id: 'cover',
    kind: 'cover',
    kicker: 'WANTAI BioPharm · Beijing · established 1991',
    title: 'A European selling deck',
    lead:
      'Infectious-disease diagnostics for European laboratories and public-health programmes — from the company that builds its tests from the molecule up.',
    footnote: 'Confidential working draft · not for external distribution',
  },
  {
    id: 'problem',
    kind: 'problem',
    kicker: 'The problem in this room',
    title:
      '39 million people live with HIV — and 5.5 million don’t know it. That gap is a testing problem.',
    points: [
      'Undiagnosed infection persists because the people most at risk don’t reach a clinic for a blood draw — the last mile of every screening programme.',
      'Latent tuberculosis sits in roughly a quarter of the world’s population. Europe screens migrants, contacts and the immunosuppressed, and needs an IGRA that scales to do it.',
      'Hepatitis E is no longer exotic — it is now among the commonest causes of acute viral hepatitis in parts of Europe, and it has entered the blood-safety conversation.',
    ],
    footnote: 'Sources: UNAIDS; WHO Global TB Report; EASL / ECDC HEV surveillance.',
  },
  {
    id: 'two-gates',
    kind: 'two-col',
    kicker: 'How Europe actually works',
    title:
      'Every supplier in Europe must clear two gates: the right to sell, and the right to be paid.',
    columns: [
      {
        head: 'The right to sell',
        body:
          'CE marking under IVDR (2017/746). The highest-risk assays — HIV, HBV, HCV — are Class D: a notified body, a full technical file, and clinical-performance evidence before a single sale.',
      },
      {
        head: 'The right to be paid',
        body:
          'Reimbursement is decided country by country — the French NABM nomenclature, Spain’s regional catalogues, Italy’s regional tenders. A CE mark opens the door; reimbursement fills the room.',
      },
    ],
    footnote:
      'The companion primers (REF-EUIVD-001…005) detail each market’s mechanics, country by country.',
  },
  {
    id: 'credentials',
    kind: 'standard',
    kicker: 'Why Wantai belongs here',
    title:
      '100+ CE marks and WHO prequalification — Wantai already meets the bars Europe requires.',
    points: [
      'A research company, not a reseller: 35 years, the NIDVD national research institute co-founded with Xiamen University, and the world’s first hepatitis E vaccine — built on the E. coli expression system.',
      'Credentials that matter here: CE-marked across the infectious-disease menu, WHO-prequalified where it counts, and specific guideline endorsements — HAS on IGRA, EASL on hepatitis E.',
    ],
    footnote:
      'WHO prequalification: HIV urine self-test (2025), HPV vaccine (2021). NIDVD: 10 research groups, 110+ scholars.',
  },
  {
    id: 'tb-igra',
    kind: 'product',
    kicker: 'Lead product · Tuberculosis',
    title:
      'One of only two WHO-recommended ELISA-IGRAs — a guideline-backed alternative to QuantiFERON.',
    points: [
      'WHO recommended Wantai’s TB-IGRA in 2022. Europe’s guidelines now prefer IGRA over the tuberculin skin test — SEPAR / SEIMC in Spain, HAS in France.',
      'It runs on the same Wan-family analyser as the rest of the menu: a laboratory adds TB-IGRA without adding a platform.',
    ],
    footnote: '130+ tests available on the Wan100 — TB-IGRA among them.',
  },
  {
    id: 'hiv-urine',
    kind: 'product',
    kicker: 'Lead product · HIV',
    title:
      'The first WHO-prequalified urine HIV self-test — 98.5–100% sensitive, and it reaches the people blood tests miss.',
    points: [
      'WHO-prequalified in 2025. A 1,495-participant field study reported 99.4% sensitivity and 100% specificity.',
      'Urine, not blood: no lancet, no phlebotomist, no clinic visit. It closes the last mile of the 95-95-95 diagnosis target.',
    ],
    footnote:
      'Wantai’s HIV kit has ranked #1 in China for 16 consecutive years. WHO PQDx 0544-005-00.',
  },
  {
    id: 'hev',
    kind: 'product',
    kicker: 'Lead product · Hepatitis E',
    title:
      'Wantai cracked how hepatitis E works — and built both the vaccine and the gold-standard test.',
    points: [
      'The HEV serology assays are the reference standard — cited by EASL and used in the research that defined the field.',
      'Hepatitis E is now among the commonest causes of acute viral hepatitis in parts of Europe: a rising clinical, and blood-safety, concern.',
    ],
    gated: true,
    gatedNote:
      'Blood-screening claim held (§7.1). European donor screening is molecular (NAT / RNA) — France went universal RNA in 2023 — not serology. What Wantai supplies to named EU blood services must be confirmed before this becomes a screening pitch.',
  },
  {
    id: 'platform',
    kind: 'platform',
    kicker: 'The platform behind the products',
    title: 'One platform, one menu, one partner — not a one-test sale.',
    points: [
      'The Wan family — Wan100, Wan200+, Wan600, WanTLA, Wan BC2800 — carries a 220-test menu across CLIA, ELISA, point-of-care, molecular (WanTag / MFCS-6 / FMNE-08) and biochemistry.',
      '3,000+ analysers installed worldwide. A laboratory that adopts one Wantai test inherits a platform it can grow on.',
    ],
    footnote:
      'The menu spans infectious disease, hepatitis, tumour, thyroid, cardiac and hormone testing.',
  },
  {
    id: 'proof',
    kind: 'proof',
    kicker: 'Proof and scale',
    title: 'Europe is an extension of a proven global footprint, not an experiment.',
    points: [
      'A research pedigree (NIDVD; 1,200+ R&D staff; ~$146M annual R&D), a manufacturing base (5 factories, dual-sited), and 3,000+ installed analysers.',
      'The service that scores in European tenders: local training, installation and response.',
    ],
    gated: true,
    gatedNote:
      'Country-reach and total-test figures shown to buyers must be reconciled first (§7.2). The corporate deck’s map supports roughly 84 countries; the “110+” and “500M+ tests” claims are not yet sourced — use the confirmed number, or none.',
  },
  {
    id: 'ask',
    kind: 'ask',
    kicker: 'The next step',
    title: 'The proposed next step: a pilot evaluation of TB-IGRA in [country].',
    points: [
      'One product, one laboratory, one guideline tailwind — the lowest-friction way to prove Wantai on European soil.',
      'From there: the platform, the wider menu, and a partnership that compounds.',
    ],
    footnote:
      'Tailor the lead product to the buyer in the room — TB laboratory, HIV public-health lead, or hepatology unit (§14).',
  },
];

const zhCN = [
  {
    id: 'cover',
    kind: 'cover',
    kicker: '万泰生物 · 北京 · 创立于 1991 年',
    title: '一份面向欧洲的销售演示',
    lead:
      '面向欧洲实验室与公共卫生项目的感染性疾病诊断——来自一家从分子层面自建检测的公司。',
    footnote: '机密工作草稿 · 不对外分发',
  },
  {
    id: 'problem',
    kind: 'problem',
    kicker: '这间屋子里的问题',
    title: '3,900 万人感染 HIV——其中 550 万人不知情。那道缺口是一个检测问题。',
    points: [
      '未确诊的感染之所以持续，是因为最高危的人群不会去诊所抽血——这是每个筛查项目的"最后一公里"。',
      '潜伏结核感染存在于全球约四分之一的人口中。欧洲对移民、接触者和免疫抑制人群进行筛查，需要一款可规模化的 IGRA 来完成。',
      '戊型肝炎不再罕见——它如今是欧洲部分地区急性病毒性肝炎最常见的病因之一，并已进入血液安全议题。',
    ],
    footnote: '来源：UNAIDS；WHO 全球结核报告；EASL / ECDC 戊肝监测。',
  },
  {
    id: 'two-gates',
    kind: 'two-col',
    kicker: '欧洲实际如何运作',
    title: '每个供应商在欧洲都必须清关两道门：销售的权利，与获得付款的权利。',
    columns: [
      {
        head: '销售的权利',
        body:
          'IVDR（2017/746）下的 CE 标志。最高风险的检测——HIV、HBV、HCV——属 D 类：售出第一份之前，需要公告机构、完整技术文件和临床性能证据。',
      },
      {
        head: '获得付款的权利',
        body:
          '报销逐国决定——法国的 NABM 目录、西班牙的地区目录、意大利的地区招标。CE 标志打开门；报销才填满房间。',
      },
    ],
    footnote: '配套入门读物（REF-EUIVD-001…005）逐国详述各市场的机制。',
  },
  {
    id: 'credentials',
    kind: 'standard',
    kicker: '万泰为何属于这里',
    title: '100+ 项 CE 标志与 WHO 预认证——万泰已达到欧洲所要求的门槛。',
    points: [
      '一家研究型公司，而非转售商：35 年积累、与厦门大学共建的国家传染病诊断试剂与疫苗工程技术研究中心（NIDVD），以及全球首个戊型肝炎疫苗——基于大肠杆菌表达系统研制。',
      '在此地重要的资质：感染菜单全线 CE 标志、在关键处获 WHO 预认证，以及具体的指南背书——HAS 对 IGRA、EASL 对戊型肝炎。',
    ],
    footnote:
      'WHO 预认证：HIV 尿液自检（2025）、HPV 疫苗（2021）。NIDVD：10 个研究组、110+ 名学者。',
  },
  {
    id: 'tb-igra',
    kind: 'product',
    kicker: '主打产品 · 结核',
    title: '全球仅两款 WHO 推荐的 ELISA-IGRA 之一——一个有指南背书、可替代 QuantiFERON 的选择。',
    points: [
      'WHO 于 2022 年推荐了万泰的 TB-IGRA。欧洲指南如今优先推荐 IGRA 而非结核菌素皮肤试验——西班牙的 SEPAR / SEIMC、法国的 HAS。',
      '它与菜单其余部分运行在同一台万系列分析仪上：实验室无需新增平台即可增加 TB-IGRA。',
    ],
    footnote: 'Wan100 上可做 130+ 项检测——TB-IGRA 是其中之一。',
  },
  {
    id: 'hiv-urine',
    kind: 'product',
    kicker: '主打产品 · HIV',
    title: '首个 WHO 预认证的尿液 HIV 自检——灵敏度 98.5–100%，触及血液检测遗漏的人群。',
    points: [
      '2025 年获 WHO 预认证。一项 1,495 名参与者的实地研究报告灵敏度 99.4%、特异性 100%。',
      '用尿液而非血液：无需采血针、无需采血人员、无需就诊。它补上了 95-95-95 诊断目标的"最后一公里"。',
    ],
    footnote: '万泰的 HIV 试剂盒在中国连续 16 年排名第一。WHO PQDx 0544-005-00。',
  },
  {
    id: 'hev',
    kind: 'product',
    kicker: '主打产品 · 戊型肝炎',
    title: '万泰破解了戊型肝炎的机制——并造出了疫苗与金标准检测。',
    points: [
      'HEV 血清学检测是参比标准——被 EASL 引用，并用于界定该领域的研究。',
      '戊型肝炎如今是欧洲部分地区急性病毒性肝炎最常见的病因之一：一个在临床与血液安全上都在上升的关切。',
    ],
    gated: true,
    gatedNote:
      '血筛主张予以保留（§7.1）。欧洲献血者筛查是分子检测（NAT / RNA）——法国于 2023 年转为全面 RNA——而非血清学。在此成为血筛话术之前，须确认万泰向被点名的欧盟血液服务机构究竟供应什么。',
  },
  {
    id: 'platform',
    kind: 'platform',
    kicker: '产品背后的平台',
    title: '一个平台、一份菜单、一个伙伴——而非一次性单项销售。',
    points: [
      '万系列——Wan100、Wan200+、Wan600、WanTLA、Wan BC2800——承载一份 220 项检测的菜单，横跨 CLIA、ELISA、即时检测、分子（WanTag / MFCS-6 / FMNE-08）和生化。',
      '全球装机 3,000+ 台分析仪。采用一款万泰检测的实验室，就继承了一个可以成长的平台。',
    ],
    footnote: '菜单涵盖感染、肝炎、肿瘤、甲状腺、心脏和激素检测。',
  },
  {
    id: 'proof',
    kind: 'proof',
    kicker: '实证与规模',
    title: '欧洲是一个已被验证的全球布局的延伸，而非一次试验。',
    points: [
      '研究积淀（NIDVD；1,200+ 研发人员；年研发投入约 1.46 亿美元）、制造基地（5 座工厂、双基地），以及 3,000+ 台装机分析仪。',
      '在欧洲招标中得分的服务：本地培训、安装与响应。',
    ],
    gated: true,
    gatedNote:
      '向买家展示的国家覆盖与总检测量数字须先核对（§7.2）。企业演示文稿的地图支持约 84 个国家；"110+"和"5 亿+ 人份检测"的说法尚无来源——用已确认的数字，或不用。',
  },
  {
    id: 'ask',
    kind: 'ask',
    kicker: '下一步',
    title: '建议的下一步：在 [国家] 对 TB-IGRA 进行一次试点评估。',
    points: [
      '一款产品、一家实验室、一股指南顺风——在欧洲土壤上证明万泰的最低摩擦方式。',
      '由此展开：平台、更广的菜单，以及一段不断累积的伙伴关系。',
    ],
    footnote:
      '按屋里的买家裁剪主打产品——结核实验室、HIV 公共卫生负责人，或肝病科（§14）。',
  },
];

const decks = { en, 'zh-cn': zhCN, 'zh-CN': zhCN };

// The one hard-coded, language-varying label the renderer needs (the gate flag)
// and the "back to the site" affordance.
export const gateFlag = {
  en: '⚑ Open item — confirm before external use',
  'zh-cn': '⚑ 未决事项——对外使用前须确认',
};
export const backLabel = { en: '‹ Back', 'zh-cn': '‹ 返回' };

// Resolve by languageroot code ('en' | 'zh-cn'), falling back to en.
export const getDeck = (lang) => decks[lang] || decks[(lang || '').toLowerCase()] || en;
export const pick = (map, lang) => map[lang] || map[(lang || '').toLowerCase()] || map.en;

export default decks;
